import { MARKER_HIT_SIZE_PX } from './markers';
import type { PixelPoint } from './types';

/**
 * A Device marker as a click target: its pin's anchor point in map-area pixels. The caller projects
 * — this layer never touches the SDK (ADR-0017).
 */
export type MarkerTarget = { id: string; point: PixelPoint };

/**
 * A Field as a click target. `rings` is the projected Boundary's rings: even-odd counting over every
 * ring at once is correct while no two of a Grower's Fields overlap — which 113586 §1b guarantees —
 * and it handles an Interior shape (a hole, a centre-pivot ring) for free.
 *
 * **One** level of nesting, unlike the admin twin's multipolygon: our contract sends a GeoJSON
 * `Polygon`, so a Field is one polygon (see `boundaries.ts`).
 *
 * `createdAt` is the ISO timestamp the payload carries for exactly this reason: every tie-break in
 * this feature resolves to the **more recently created** Field (113586 §3i).
 */
export type FieldTarget = {
  id: string;
  createdAt: string;
  rings: readonly (readonly PixelPoint[])[];
};

export type MapClickHit =
  | { kind: 'device'; id: string }
  | { kind: 'field'; id: string }
  | { kind: 'none' };

/**
 * Pointer slop around a marker (113586 §6c: "a near miss does not silently focus the Field instead").
 *
 * **Derived, not chosen.** `MARKER_HIT_SIZE_PX` is the marker `<button>`'s box, which
 * `device-markers.module.scss` rounds to a circle, so the browser hit-tests a circle of half that
 * radius about the Device's point. This arbitration has to answer `device` over exactly the same disc:
 * any smaller and the annulus between the two is a dead zone where the press lands on the `<button>`
 * — which hit-tests first and swallows the event — while this function, asked the same question, would
 * have answered `field`. `markers.ts` records the same contract from its own side; importing the size
 * is what keeps the two from drifting apart.
 */
export const MARKER_HIT_RADIUS = MARKER_HIT_SIZE_PX / 2;

/** Pointer slop around a Boundary edge, for a click that lands in no interior (113586 §3i). */
export const EDGE_HIT_TOLERANCE = 6;

const EPSILON = 1e-9;

/**
 * What a click hit. §6a and §3h/§3i are the same question asked twice, so they are one function: a
 * marker within tolerance wins over any polygon beneath it, and only then is the Boundary set asked.
 */
export function resolveMapClick(
  click: PixelPoint,
  targets: { markers: readonly MarkerTarget[]; fields: readonly FieldTarget[] },
): MapClickHit {
  const marker = nearestMarker(click, targets.markers);
  if (marker) return { kind: 'device', id: marker.id };

  const fieldId = resolveFieldAt(click, targets.fields, EDGE_HIT_TOLERANCE);
  return fieldId === null ? { kind: 'none' } : { kind: 'field', id: fieldId };
}

/**
 * Which Field a point belongs to. One function answers three criteria, because they are one rule: a
 * point inside exactly one Boundary (113586 §3h), a point on a Boundary line, which is inclusive, and
 * a point on an edge two Fields share — or, against the non-overlap invariant, inside two at once —
 * both of which resolve to the **more recently created** (§3i).
 *
 * Private: `resolveMapClick` is this module's whole public surface today. Export it when a second
 * caller genuinely exists.
 */
function resolveFieldAt(
  point: PixelPoint,
  fields: readonly FieldTarget[],
  tolerance = 0,
): string | null {
  const inside = fields.filter((field) => isInsideRings(point, field.rings));
  if (inside.length > 0) return mostRecent(inside).id;

  const near = fields.filter((field) => distanceToRings(point, field.rings) <= tolerance);
  return near.length > 0 ? mostRecent(near).id : null;
}

/**
 * Whether a point lies **strictly inside** one of the Grower's saved Fields — the **exclusive**-edge
 * sibling of {@link resolveFieldAt}, and the only overlap question the Field Mapping pre-filter is
 * allowed to ask (ADR-0018).
 *
 * The two differ on the edge, and the difference is the point of the function. `resolveFieldAt` is
 * inclusive because a click on a Boundary line should focus that Field. Placement is the opposite
 * rule: the backend's own source records "CONTACT NEVER REJECTS. Only shared area is an overlap; a
 * shared edge or vertex is fine", confirmed by the BA on 2026-08-11. Using the inclusive test as a
 * pre-filter would make this client stricter than its own server — the live defect the admin twin
 * carries, recorded in ADR-0018's Alternatives so it is not "fixed" back into ours.
 *
 * Called in the `{ x: lon, y: lat }` geographic plane at zero tolerance, as `resolveFieldAt`
 * documents: the parity maths is handedness-agnostic, and a tolerance in degrees is meaningless.
 */
export function isStrictlyInsideAnyField(
  point: PixelPoint,
  fields: readonly FieldTarget[],
): boolean {
  return fields.some((field) => isStrictlyInsideRings(point, field.rings));
}

/**
 * Whether a point lies **strictly inside** a set of rings — edges and vertices excluded.
 *
 * Exported for **Placement mode** (113609 user story 32), which asks this of **one Imported
 * shape's** rings rather than of the Grower's saved Fields, so {@link isStrictlyInsideAnyField}'s
 * `FieldTarget[]` shape does not fit. Same function, same maths, same zero tolerance, reused
 * **verbatim** on purpose: loosening it or re-deriving it elsewhere is exactly how a client becomes
 * stricter than its own server — the live defect ADR-0018's Alternatives records in the admin twin
 * and instructs us not to reproduce.
 *
 * Called in the `{ x: lon, y: lat }` geographic plane; the parity maths is handedness-agnostic.
 */
export function isStrictlyInsideRings(
  point: PixelPoint,
  rings: readonly (readonly PixelPoint[])[],
): boolean {
  return !isOnRings(point, rings) && crossesOddParity(point, rings);
}

/** Later `createdAt` wins; an exact tie falls to the larger id, so the answer is always the same. */
function mostRecent(fields: readonly FieldTarget[]): FieldTarget {
  return fields.reduce((best, field) => {
    const a = Date.parse(field.createdAt);
    const b = Date.parse(best.createdAt);
    if (a > b) return field;
    if (a === b && field.id > best.id) return field;
    return best;
  });
}

function nearestMarker(
  click: PixelPoint,
  markers: readonly MarkerTarget[],
): MarkerTarget | undefined {
  let best: { marker: MarkerTarget; distance: number } | undefined;

  for (const marker of markers) {
    const distance = Math.hypot(marker.point.x - click.x, marker.point.y - click.y);
    if (distance > MARKER_HIT_RADIUS) continue;
    // Nearest wins; two markers exactly equidistant fall to the smaller id, so the answer never
    // depends on the order the caller happened to hold its markers in.
    if (
      !best ||
      distance < best.distance ||
      (distance === best.distance && marker.id < best.marker.id)
    ) {
      best = { marker, distance };
    }
  }

  return best?.marker;
}

/** Even-odd ray casting, with an explicit on-segment test so a Boundary line counts as inside. */
function isInsideRings(point: PixelPoint, rings: readonly (readonly PixelPoint[])[]): boolean {
  return isOnRings(point, rings) || crossesOddParity(point, rings);
}

function isOnRings(point: PixelPoint, rings: readonly (readonly PixelPoint[])[]): boolean {
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i += 1) {
      if (isOnSegment(point, ring[(i + ring.length - 1) % ring.length], ring[i])) return true;
    }
  }
  return false;
}

function crossesOddParity(point: PixelPoint, rings: readonly (readonly PixelPoint[])[]): boolean {
  let inside = false;

  for (const ring of rings) {
    for (let i = 0; i < ring.length; i += 1) {
      const curr = ring[i];
      const prev = ring[(i + ring.length - 1) % ring.length];

      if (curr.y > point.y !== prev.y > point.y) {
        const xCross = ((prev.x - curr.x) * (point.y - curr.y)) / (prev.y - curr.y) + curr.x;
        if (point.x < xCross) inside = !inside;
      }
    }
  }

  return inside;
}

function distanceToRings(point: PixelPoint, rings: readonly (readonly PixelPoint[])[]): number {
  let nearest = Number.POSITIVE_INFINITY;

  for (const ring of rings) {
    for (let i = 0; i < ring.length; i += 1) {
      const curr = ring[i];
      const prev = ring[(i + ring.length - 1) % ring.length];
      nearest = Math.min(nearest, distanceToSegment(point, prev, curr));
    }
  }

  return nearest;
}

function isOnSegment(point: PixelPoint, a: PixelPoint, b: PixelPoint): boolean {
  const cross = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
  if (Math.abs(cross) > EPSILON) return false;

  return (
    point.x >= Math.min(a.x, b.x) - EPSILON &&
    point.x <= Math.max(a.x, b.x) + EPSILON &&
    point.y >= Math.min(a.y, b.y) - EPSILON &&
    point.y <= Math.max(a.y, b.y) + EPSILON
  );
}

function distanceToSegment(point: PixelPoint, a: PixelPoint, b: PixelPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(point.x - a.x, point.y - a.y);

  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}
